import * as React from 'react';
import classnames from 'classnames';
import moment from 'moment-timezone';

import styles from './index.module.css';
import { padStartWithZero } from '../../utils';

///////////////////////////////////////////////////
//////INTERFACES

export type Unit = 'month' | 'day' | 'year';

export interface DateComponents {
  month: string;
  day: string;
  year: string;
}

interface LoginProps {
  monthInput?: React.RefObject<HTMLInputElement>;
  dayInput?: React.RefObject<HTMLInputElement>;
  yearInput?: React.RefObject<HTMLInputElement>;
}

interface Props extends LoginProps {
  borderColor?: string;
  className?: string;
  curvedInput?: number;
  date?: string;
  placeholder?: string;
  isLogin?: boolean;
  isRegistration?: boolean;
  onChange: (value: Date) => void;
}

interface State {
  selectedDate: DateComponents;
  focusedUnit: Unit | undefined;
}

///////////////////////////////////////////////////
//////UTILS

function isDifferentDate(
  oldDate: DateComponents,
  newDate: DateComponents,
): boolean {
  return (
    oldDate.month !== newDate.month ||
    oldDate.day !== newDate.day ||
    oldDate.year !== newDate.year
  );
}

function isDeletingValue(
  oldDate: DateComponents,
  newDate: DateComponents,
): boolean {
  const monthValueDeleted = oldDate.month.length > newDate.month.length;
  const dayValueDeleted = oldDate.day.length > newDate.day.length;
  const yearValueDeleted = oldDate.year.length > newDate.year.length;
  return monthValueDeleted || dayValueDeleted || yearValueDeleted;
}

function setMonthValue(value: string, prevValue: string): string {
  if (value.length < prevValue.length) {
    return value;
  }
  if (value[0] > '1') {
    return '0' + value[0];
  } else {
    if (value.length === 2) {
      const int = parseInt(value, 10);

      if (int < 1) return '01';
      if (int > 12) return '12';
    }

    return value.slice(0, 2);
  }
}

function setDayValue(value: string, prevValue: string): string {
  if (value.length < prevValue.length) {
    return value;
  }
  if (value[0] > '3') {
    return '0' + value[0];
  } else {
    if (value.length === 2) {
      const int = parseInt(value, 10);

      if (int < 1) return '01';
      if (int > 31) return '31';
    }

    return value.slice(0, 2);
  }
}

function setYearValue(value: string, prevValue: string): string {
  if (value.length < prevValue.length) {
    return value;
  }
  let yearValue = '';
  if (['2', '1'].includes(value[0])) {
    yearValue = value;
  }
  return yearValue.slice(0, 4);
}

function parseDate(date?: string): DateComponents {
  if (!date) {
    return {
      month: '',
      day: '',
      year: '',
    };
  }

  const parsedDate = moment(new Date(date)).tz('utc').locale('en');

  return {
    month: parsedDate.format('MM'),
    day: parsedDate.format('DD'),
    year: parsedDate.format('YYYY'),
  };
}

///////////////////////////////////////////////////
//////COMPONENT

function Placeholder(props: {
  placeholder: string;
  onClick: () => Unit | undefined;
}) {
  return (
    <input
      type="number"
      pattern="[0-9]*"
      className={classnames(styles.PlaceholderInput, styles.Input)}
      placeholder={props.placeholder}
      onClick={props.onClick}
      readOnly={true}
    />
  );
}

export class DatePicker extends React.Component<Props, State> {
  private monthInput = React.createRef<HTMLInputElement>();
  private dayInput = React.createRef<HTMLInputElement>();
  private yearInput = React.createRef<HTMLInputElement>();

  constructor(props: Props) {
    super(props);
    const { date } = this.props;
    this.state = {
      selectedDate: parseDate(date),
      focusedUnit: undefined,
    };
    if (props.monthInput) this.monthInput = props.monthInput;
    if (props.dayInput) this.dayInput = props.dayInput;
    if (props.yearInput) this.yearInput = props.yearInput;
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    const { date } = this.props;
    if (prevProps.date !== date) {
      this.setState({
        selectedDate: parseDate(date),
      });
    }

    // check which input to focus
    const { selectedDate } = this.state;
    let focusedUnitUpdate: Unit | undefined;
    if (isDifferentDate(prevState.selectedDate, selectedDate)) {
      focusedUnitUpdate = isDeletingValue(prevState.selectedDate, selectedDate)
        ? this.findInputToFocusOnWhenDeleting()
        : this.findInputToFocusOnWhenAppending();
    }

    if (this.state.focusedUnit !== prevState.focusedUnit) {
      this.focusInput();
      return;
    }
    if (isDifferentDate(prevState.selectedDate, selectedDate)) {
      this.focusInput(focusedUnitUpdate);
    }
  }

  focusInput = (focusedUnitUpdate?: Unit) => {
    const { focusedUnit } = this.state;
    const unitToFocus = focusedUnitUpdate || focusedUnit;
    let node: HTMLInputElement | null = null;
    if (unitToFocus === 'month') {
      node = this.monthInput.current;
    } else if (unitToFocus === 'day') {
      node = this.dayInput.current;
    } else if (unitToFocus === 'year') {
      node = this.yearInput.current;
    }
    if (node) {
      node.focus();
    }
  };

  findInputToFocusOnWhenDeleting = (): Unit | undefined => {
    const {
      selectedDate: { day, year },
    } = this.state;
    let focusedUnit: Unit | undefined;
    if (year.length > 0) {
      focusedUnit = 'year';
    } else if (day.length > 0) {
      focusedUnit = 'day';
    } else {
      focusedUnit = 'month';
    }
    if (focusedUnit) {
      this.setState({ focusedUnit });
      return focusedUnit;
    }
    return;
  };

  findInputToFocusOnWhenAppending = (): Unit | undefined => {
    const {
      selectedDate: { month, day, year },
    } = this.state;
    let focusedUnit: Unit | undefined;
    if (month.length < 2) {
      focusedUnit = 'month';
    } else if (day.length < 2) {
      focusedUnit = 'day';
    } else if (year.length < 4) {
      focusedUnit = 'year';
    }
    if (focusedUnit) {
      this.setState({ focusedUnit });
      return focusedUnit;
    }
    return;
  };

  handleChange = (event: React.FormEvent<HTMLInputElement>) => {
    const unit = event.currentTarget.name;
    if (unit === 'day' || unit === 'month' || unit === 'year') {
      this.setDate(unit, event.currentTarget.value);
    }
  };

  handleFocus = (event: React.FormEvent<HTMLInputElement>) => {
    const unit = event.currentTarget.name;
    if (unit === 'day' || unit === 'month' || unit === 'year') {
      this.setState({
        focusedUnit: unit,
      });
    }
  };

  handleBlur = () => {
    this.setState({
      focusedUnit: undefined,
    });
  };

  setDate = (unit: Unit, value: string) => {
    const { month, day, year } = this.state.selectedDate;
    const updatedDate = { ...this.state.selectedDate };
    if (unit === 'month') {
      updatedDate[unit] = setMonthValue(value, month);
    } else if (unit === 'day') {
      updatedDate['month'] = padStartWithZero(month, 2);
      updatedDate[unit] = setDayValue(value, day);
    } else if (unit === 'year') {
      updatedDate['month'] = padStartWithZero(month, 2);
      updatedDate['day'] = padStartWithZero(day, 2);
      updatedDate[unit] = setYearValue(value, year);
    }
    this.setState({ selectedDate: updatedDate });
    const constructed = constructDate(updatedDate);
    if (constructed) {
      this.props.onChange(constructed);
    }
  };

  handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    previousFocus?: Unit,
    nextFocus?: Unit,
  ) => {
    const { keyCode } = event;
    if (['-', '+'].includes(event.key)) return event.preventDefault();
    const valid = keyCode > 47 && keyCode < 58; // number keys
    if (valid && nextFocus && event.currentTarget.value.length === 2) {
      this.focusInput(nextFocus);
    } else if (previousFocus && keyCode === 8 && !event.currentTarget.value) {
      this.focusInput(previousFocus);
    }
  };

  render() {
    const {
      className,
      placeholder,
      curvedInput,
      borderColor,
      isLogin,
      isRegistration,
    } = this.props;
    const {
      selectedDate: { month, day, year },
    } = this.state;
    const { focusedUnit } = this.state;
    const hasDate = month !== '' || day !== '' || year !== '';
    const shouldDisplayPlaceholder = !(focusedUnit || hasDate);

    const style = {};
    if (borderColor) Object.assign(style, { borderColor });

    return (
      <div
        className={classnames('date-picker', className, styles.Wrapper, {
          [styles.Login]: isLogin,
          [styles.Registration]: isRegistration,
        })}
      >
        {placeholder && shouldDisplayPlaceholder && (
          <Placeholder
            placeholder={placeholder}
            onClick={this.findInputToFocusOnWhenAppending}
          />
        )}
        <div className={styles.DateInputs}>
          <input
            name="month"
            type="number"
            pattern="[0-9]*"
            maxLength={2}
            ref={this.monthInput}
            className={classnames(styles.Input, styles.MonthInput)}
            style={
              curvedInput
                ? Object.assign({}, style, {
                    borderRadius: `${curvedInput}px 0px 0px ${curvedInput}px`,
                  })
                : style
            }
            placeholder="MM"
            value={month}
            onBlur={this.handleBlur}
            onFocus={this.handleFocus}
            onChange={this.handleChange}
            onKeyDown={(e) => this.handleKeyDown(e, undefined, 'day')}
          />
          <div className={styles.Slash} style={style}>
            /
          </div>
          <input
            name="day"
            type="number"
            pattern="[0-9]*"
            maxLength={2}
            ref={this.dayInput}
            className={classnames(styles.Input, styles.DayInput)}
            style={style}
            value={day}
            placeholder="DD"
            onBlur={this.handleBlur}
            onFocus={this.handleFocus}
            onChange={this.handleChange}
            onKeyDown={(e) => this.handleKeyDown(e, 'month', 'year')}
          />
          <div className={styles.Slash} style={style}>
            /
          </div>
          <input
            name="year"
            type="number"
            pattern="[0-9]*"
            maxLength={4}
            ref={this.yearInput}
            className={classnames(styles.Input, styles.YearInput)}
            style={
              curvedInput
                ? Object.assign({}, style, {
                    borderRadius: ` 0px ${curvedInput}px ${curvedInput}px  0px`,
                  })
                : style
            }
            value={year}
            placeholder="YYYY"
            onBlur={this.handleBlur}
            onFocus={this.handleFocus}
            onChange={this.handleChange}
            onKeyDown={(e) => this.handleKeyDown(e, 'day')}
          />
        </div>
      </div>
    );
  }
}

export const constructDate = (parts: DateComponents): Date | undefined => {
  const { month, day, year } = parts;
  if (!month || !day || !year || year.length < 4) {
    return;
  }

  return moment([year, month, day].join('-')).locale('en').toDate();
};
